import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
  Toggle,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import '../screens-2c.css';
import { ProtectedLists } from './ProtectedLists.js';

type Kind = 'lists' | 'contacts' | 'sent';

interface JobStatus {
  kind: Kind;
  label: string;
  status: 'idle' | 'running' | 'ok' | 'failed' | 'skipped' | 'waiting';
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastOkAt: string | null;
  dataAsOf: string | null;
  delayMinutes: number | null;
  nextDueAt: string | null;
  intervalMinutes: number;
  stale: boolean;
  consecutiveFailures: number;
  lastError: string | null;
  note: string | null;
  counts: Record<string, number>;
  running: {
    runId: string;
    phase: string;
    progress: { phases: Array<{ phase: string; done: number; total: number }> };
  } | null;
}
interface BrandStatus {
  brand: string;
  name: string;
  sharedWith: string[];
  hasAccess: boolean;
  jobs: JobStatus[];
  reactions: {
    lastRunAt: string | null;
    lastEventAt: string | null;
    lastError: string | null;
    counts: Record<string, number> | null;
    stale: boolean;
  };
}
interface SyncData {
  now: string;
  layerActive: boolean;
  enabled: boolean;
  automatic: boolean;
  settings: {
    listsHours: number;
    contactsHours: number;
    sentMinutes: number;
    sentWindowDays: number;
    nightWindow: { fromHour: number; toHour: number };
    createContacts: boolean;
  };
  brands: BrandStatus[];
  problems: number;
}

const STATUS: Record<JobStatus['status'], { label: string; tone: Tone }> = {
  ok: { label: 'v pořádku', tone: 'ok' },
  running: { label: 'běží', tone: 'acc' },
  failed: { label: 'selhalo', tone: 'bad' },
  skipped: { label: 'přeskočeno', tone: 'warn' },
  idle: { label: 'čeká', tone: 'neu' },
  waiting: { label: 'ještě neběželo', tone: 'neu' },
};

const PHASE: Record<string, string> = {
  catalog: 'seznamy a pole',
  emails: 'e-maily',
  contacts: 'kontakty',
  unsubscribed: 'odhlášení',
  purposes: 'účely',
  match: 'spárování s hubem',
  events: 'reakce',
  sent: 'odeslané e-maily',
};

/** Čísla, která se u jednotlivých druhů ukazují (česky). */
const COUNTS: Record<Kind, Array<[string, string]>> = {
  lists: [
    ['lists', 'Seznamů'],
    ['customFields', 'Vlastních polí'],
    ['purposes', 'Účelů'],
  ],
  contacts: [
    ['contacts', 'Kontaktů ve zrcadle'],
    ['blacklisted', 'Blokovaných'],
    ['withdrawn', 'Souhlasů odvoláno'],
    ['removedFromMirror', 'Ubylo ze SE'],
  ],
  sent: [
    ['sentCampaigns', 'Kampaní'],
    ['sentEvents', 'Událostí přečteno'],
    ['sentCreated', 'Nových záznamů zpráv'],
    ['sentAlready', 'Už známých'],
    ['sentCampaignErrors', 'Kampaní s chybou'],
  ],
};

/** Chyba lidsky: co se stalo a co s tím. */
export function friendlyError(raw: string | null): string | null {
  if (!raw) return null;
  if (/429|too many|rate/i.test(raw))
    return 'SmartEmailing omezil rychlost dotazů (429). Hub počká a zkusí to znovu s delší prodlevou, nic dělat nemusíte.';
  if (/401|403|unauthori|forbidden|credential|přihlášen/i.test(raw))
    return 'SmartEmailing odmítl přihlášení. Zkontrolujte přístupy v Nastavení → Napojení.';
  if (/timeout|ETIMEDOUT|ECONN|ENOTFOUND|fetch failed|5\d\d/i.test(raw))
    return `SmartEmailing neodpovídá nebo hlásí chybu serveru. Hub to zkusí znovu. (${raw.slice(0, 160)})`;
  return raw.slice(0, 300);
}

function delayText(minutes: number | null): string {
  if (minutes === null) return 'zatím nikdy';
  if (minutes < 2) return 'čerstvá';
  if (minutes < 120) return `před ${num(minutes)} min`;
  if (minutes < 48 * 60) return `před ${num(Math.round(minutes / 60))} h`;
  return `před ${num(Math.round(minutes / 1440))} dny`;
}

interface DeltaCounts {
  seen: number;
  created: number;
  changed: number;
  unchanged: number;
  blacklisted: number;
  unsubscribed: number;
  hardbounced: number;
  withdrawn: number;
  activity: number;
}
interface DeltaInfo {
  lastRunAt: string;
  lastOkAt: string | null;
  durationMs: number;
  truncated: boolean;
  error: string | null;
  counts: DeltaCounts;
}
interface DeltaData {
  freshness: { enforced: boolean; stale: boolean; ageMinutes: number | null; maxMinutes: number };
  brands: Array<{ brand: string; daily?: DeltaInfo; optout?: DeltaInfo }>;
}

/** Přírůstkové běhy (D-334): co se stáhlo od posledně a jak jsou data stará. */
function DeltaPanel({ tz, canWrite }: { tz: string; canWrite: boolean }): JSX.Element | null {
  const client = useQueryClient();
  const q = useQuery<DeltaData>({
    queryKey: ['se-sync-delta'],
    queryFn: () => api.get('/api/se-sync/delta'),
    refetchInterval: 15_000,
  });
  const run = useMutation({
    mutationFn: (mode: 'daily' | 'optout') => api.post('/api/se-sync/delta/run', { mode }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['se-sync-delta'] }),
  });
  if (!q.data) return null;
  const f = q.data.freshness;
  const row = (label: string, i: DeltaInfo | undefined): JSX.Element => (
    <tr>
      <td>
        <b>{label}</b>
      </td>
      <td>
        {i ? (
          <StatusBadge tone={i.error ? 'bad' : 'ok'}>{i.error ? 'chyba' : 'v pořádku'}</StatusBadge>
        ) : (
          <span className="muted">ještě neběžel</span>
        )}
        {i?.error ? (
          <small className="muted">
            <br />
            {i.error}
          </small>
        ) : null}
        {i?.truncated ? (
          <small className="muted">
            <br />
            nedočtený, naváže příště
          </small>
        ) : null}
      </td>
      <td>{i?.lastOkAt ? dateTime(i.lastOkAt, tz) : '-'}</td>
      <td>
        {i
          ? `nové ${num(i.counts.created)}, změněné ${num(i.counts.changed)}, odhlášené ${num(i.counts.unsubscribed)}, blokované ${num(i.counts.blacklisted)}, tvrdé odskoky ${num(i.counts.hardbounced)}, bez změny ${num(i.counts.unchanged)}`
          : '-'}
      </td>
    </tr>
  );
  return (
    <Panel
      title="Denní přírůstek a odhlášení"
      subtitle={
        f.enforced
          ? `Data o odhlášení jsou stará ${num(f.ageMinutes ?? 0)} min (limit ${num(f.maxMinutes)} min).${f.stale ? ' Marketing a rozesílky jsou zadržené, dokud lehký běh neproběhne.' : ''}`
          : 'Zatím neproběhl žádný přírůstkový běh; hub vychází z posledního plného stažení.'
      }
      action={
        canWrite ? (
          <>
            <button
              type="button"
              className="btn"
              disabled={run.isPending}
              onClick={() => run.mutate('optout')}
            >
              Spustit lehký běh
            </button>{' '}
            <button
              type="button"
              className="btn"
              disabled={run.isPending}
              onClick={() => run.mutate('daily')}
            >
              Spustit denní přírůstek
            </button>
          </>
        ) : undefined
      }
    >
      {f.stale ? (
        <p className="note bad" role="alert">
          Odhlášení nejsou čerstvá, marketing je zadržený.
        </p>
      ) : null}
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Běh</th>
              <th>Stav</th>
              <th>Naposledy v pořádku</th>
              <th>Změny při posledním běhu</th>
            </tr>
          </thead>
          <tbody>
            {q.data.brands.flatMap((b) => [
              <tr key={`${b.brand}-h`}>
                <td colSpan={4}>
                  <small className="muted">{b.brand}</small>
                </td>
              </tr>,
              <Fragment key={`${b.brand}-d`}>{row('Denní přírůstek', b.daily)}</Fragment>,
              <Fragment key={`${b.brand}-o`}>{row('Odhlášení a blokace', b.optout)}</Fragment>,
            ])}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export function SeSync({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const tz = me.defaultTimezone;
  const canWrite = rights.write('settings');
  const [ask, setAsk] = useState<{
    kind: Kind;
    brand: string;
    label: string;
    full?: boolean;
  } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery<SyncData>({
    queryKey: ['se-sync'],
    queryFn: () => api.get('/api/se-sync'),
    refetchInterval: 10_000,
  });
  const run = useMutation({
    mutationFn: (v: { kind: Kind; brand: string; fullHistory?: boolean }) =>
      api.post<{ started: number; skipped: number }>('/api/se-sync/run', v),
    onSuccess: (r) => {
      setError(null);
      setNote(
        r.started > 0
          ? 'Synchronizace je spuštěná, worker ji za chvíli posune. Průběh uvidíte tady.'
          : 'Nic se nespustilo: přístupy chybí, nebo na značce právě běží jiný import.',
      );
      void client.invalidateQueries({ queryKey: ['se-sync'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const rematch = useMutation({
    mutationFn: (brand: string) =>
      api.post<{
        linked: number;
        mirror: number;
        matched: number;
        unmatched: number;
        blocked: number;
      }>('/api/se-sync/rematch', { brand }),
    onSuccess: (r) => {
      setError(null);
      setNote(
        `Přepárováno: nově ${num(r.linked)}; spárováno ${num(r.matched)} z ${num(r.mirror)}, nespárováno ${num(r.unmatched)}, z toho spárovaných blokovaných nebo odhlášených ${num(r.blocked)}. Souhlasy a blokace se promítnou při další synchronizaci kontaktů.`,
      );
      void client.invalidateQueries({ queryKey: ['se-sync'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const save = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.patch('/api/settings', v),
    onSuccess: () => {
      setError(null);
      setNote('Nastavení uloženo.');
      void client.invalidateQueries({ queryKey: ['se-sync'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (query.isLoading) return <Loading what="stav synchronizace" />;
  if (query.isError) {
    const code = (query.error as { code?: string }).code;
    if (code === 'layer_disabled')
      return (
        <EmptyState title="Synchronizace SmartEmailingu není zapnutá" icon="info">
          Patří do vlny Marketing. Zapněte ji v Nastavení → Vlny nasazení; do té doby se ze
          SmartEmailingu nic samo nestahuje.
        </EmptyState>
      );
    return (
      <ErrorState
        title="Stav synchronizace se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data = query.data;
  if (!data) return <Loading />;

  return (
    <>
      <PageHeader
        level={2}
        title="Synchronizace SmartEmailingu"
        description="Hub si sám a pravidelně stahuje ze SmartEmailingu seznamy, kontakty, odeslané e-maily a reakce, aby šel používat místo něj. Jen čtení: do SmartEmailingu se tady nikdy nic nezapisuje ani neodesílá."
      />
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      {error ? (
        <p className="note bad" role="alert">
          {error}
        </p>
      ) : null}

      <div className="tiles">
        <Tile
          label="Automaticky"
          value={
            data.enabled && data.automatic ? 'zapnuto' : data.enabled ? 'jen ručně' : 'vypnuto'
          }
          note={
            data.automatic
              ? 'běží sama podle intervalů níže'
              : 've Vývoji (simulované hodiny) se sama nespouští, jen tlačítkem'
          }
          icon="refresh"
        />
        <Tile
          label="Problémy"
          value={num(data.problems)}
          bad={data.problems > 0}
          note={data.problems > 0 ? 'zastaralá data nebo opakované chyby' : 'všechno čerstvé'}
          icon="info"
        />
      </div>

      <DeltaPanel tz={tz} canWrite={canWrite} />

      {data.brands.length === 0 ? (
        <EmptyState title="Žádná značka" icon="info">
          Nejdřív zadejte značky a přístupy ke SmartEmailingu v Nastavení → Značky a Napojení.
        </EmptyState>
      ) : null}

      {data.brands.map((b) => (
        <Panel
          key={b.brand}
          action={
            canWrite ? (
              <button
                type="button"
                className="btn"
                disabled={rematch.isPending}
                onClick={() => rematch.mutate(b.brand)}
                title="Spáruje zrcadlo kontaktů SmartEmailingu s kontakty hubu podle e-mailu, bez nového stahování"
              >
                Přepárovat kontakty
              </button>
            ) : undefined
          }
          title={
            b.sharedWith.length > 0
              ? `Účet SmartEmailing (${[b.brand, ...b.sharedWith].join(', ')})`
              : `${b.name} (${b.brand})`
          }
          subtitle={
            b.sharedWith.length > 0
              ? 'Značky sdílejí jeden účet SmartEmailing, proto se čte jednou za účet a čísla platí pro všechny uvedené značky.'
              : undefined
          }
        >
          {!b.hasAccess ? (
            <p className="note bad">
              Chybí přístupy ke SmartEmailingu, synchronizace se přeskakuje. Zadejte je v Nastavení
              → Napojení.
            </p>
          ) : null}
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Co</th>
                  <th>Stav</th>
                  <th>Naposledy</th>
                  <th>Data</th>
                  <th>Příště</th>
                  <th>Počty</th>
                  {canWrite ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {b.jobs.map((j) => (
                  <tr key={j.kind}>
                    <td>
                      <b>{j.label}</b>
                      <br />
                      <small className="muted">
                        každých{' '}
                        {j.intervalMinutes >= 120
                          ? `${num(Math.round(j.intervalMinutes / 60))} h`
                          : `${num(j.intervalMinutes)} min`}
                      </small>
                    </td>
                    <td>
                      <StatusBadge tone={j.stale ? 'bad' : STATUS[j.status].tone}>
                        {j.stale ? 'zastaralé' : STATUS[j.status].label}
                      </StatusBadge>
                      {j.running ? (
                        <small className="muted">
                          <br />
                          {PHASE[j.running.phase] ?? j.running.phase}
                          {j.running.progress.phases.at(-1)?.total
                            ? `: ${num(j.running.progress.phases.at(-1)?.done ?? 0)} z ${num(j.running.progress.phases.at(-1)?.total ?? 0)}`
                            : ''}
                        </small>
                      ) : null}
                    </td>
                    <td>{j.lastOkAt ? dateTime(j.lastOkAt, tz) : '—'}</td>
                    <td>{delayText(j.delayMinutes)}</td>
                    <td>{j.nextDueAt && b.hasAccess ? dateTime(j.nextDueAt, tz) : '—'}</td>
                    <td>
                      {COUNTS[j.kind]
                        .filter(([k]) => j.counts[k] !== undefined)
                        .map(([k, label]) => (
                          <div key={k}>
                            {label}: <b>{num(j.counts[k] ?? 0)}</b>
                          </div>
                        ))}
                      {(j.counts.errors ?? 0) > 0 ? (
                        <div className="muted">Dílčích chyb: {num(j.counts.errors ?? 0)}</div>
                      ) : null}
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="row">
                          <button
                            type="button"
                            className="btn"
                            disabled={run.isPending || j.status === 'running'}
                            aria-label={`Spustit teď: ${j.label}, ${b.brand}`}
                            onClick={() => setAsk({ kind: j.kind, brand: b.brand, label: j.label })}
                          >
                            Spustit teď
                          </button>
                          {j.kind === 'sent' ? (
                            <button
                              type="button"
                              className="btn"
                              disabled={run.isPending || j.status === 'running'}
                              aria-label={`Stáhnout celou historii odeslaných e-mailů, ${b.brand}`}
                              onClick={() =>
                                setAsk({
                                  kind: 'sent',
                                  brand: b.brand,
                                  label: 'Celá historie odeslaných e-mailů',
                                  full: true,
                                })
                              }
                            >
                              Celá historie
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {b.jobs
            .filter((j) => j.lastError || j.note)
            .map((j) => (
              <p key={j.kind} className={j.lastError ? 'note bad' : 'note'}>
                <b>{j.label}:</b>{' '}
                {j.lastError
                  ? `${friendlyError(j.lastError)} (${num(j.consecutiveFailures)}× po sobě)`
                  : j.note}
              </p>
            ))}
          <p className="note">
            Reakce (doručení, otevření, kliky, odskoky, odhlášení) se stahují každých 15 minut:{' '}
            {b.reactions.lastRunAt ? (
              <>
                naposledy {dateTime(b.reactions.lastRunAt, tz)}
                {b.reactions.stale ? ' — zastaralé' : ''}
                {b.reactions.counts
                  ? `, nových ${num(b.reactions.counts.applied ?? 0)} z ${num(b.reactions.counts.read ?? 0)}`
                  : ''}
                .
              </>
            ) : (
              'zatím neproběhlo.'
            )}
            {b.reactions.lastError ? ` Chyba: ${friendlyError(b.reactions.lastError)}` : ''}
          </p>
        </Panel>
      ))}

      <Panel
        title="Nastavení"
        subtitle="Bezpečné výchozí hodnoty; ruční spuštění intervaly neomezují."
      >
        <SettingsForm
          data={data}
          canWrite={canWrite}
          save={(v) => save.mutate(v)}
          busy={save.isPending}
        />
      </Panel>

      <Panel title="Co se čte a co ne">
        <ul className="plain">
          <li>
            Čte se: seznamy kontaktů, vlastní pole a účely, kontakty s profily a odhlášením,
            historie odeslaných e-mailů po kampaních a jejich reakce.
          </li>
          <li>
            Do SmartEmailingu se <b>nikdy nezapisuje</b> a nic se neodesílá. Odeslané e-maily se v
            hubu ukládají jen jako záznam „zaznamenáno“. Pole <code>Ecomail - tags</code> se
            nedotýká nic.
          </li>
          <li>
            Odhlášení a blokace ve SmartEmailingu se promítnou jako odvolaný souhlas. Souhlas se z
            této synchronizace nikdy nepřidělí.
          </li>
          <li>
            Selže-li jedna kampaň nebo seznam, ostatní se načtou. Po chybě hub čeká 15 minut až 4
            hodiny; při omezení rychlosti (429) tedy SmartEmailing nezahlcuje.
          </li>
        </ul>
      </Panel>

      <ConfirmDialog
        open={ask !== null}
        title={ask?.full ? 'Stáhnout celou historii?' : 'Spustit synchronizaci?'}
        confirmLabel="Spustit"
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          if (ask)
            run.mutate({
              kind: ask.kind,
              brand: ask.brand,
              ...(ask.full ? { fullHistory: true } : {}),
            });
          setAsk(null);
        }}
      >
        {ask?.full
          ? `Stáhne se historie odeslaných e-mailů až 36 měsíců zpět (značka ${ask?.brand}). Může trvat dlouho a čerpá limity SmartEmailingu; jen čtení.`
          : `Spustí se „${ask?.label ?? ''}“ pro značku ${ask?.brand ?? ''} hned, mimo interval. Jen čtení ze SmartEmailingu.`}
      </ConfirmDialog>
      <ProtectedLists canWrite={canWrite} />
    </>
  );
}

function SettingsForm({
  data,
  canWrite,
  save,
  busy,
}: {
  data: SyncData;
  canWrite: boolean;
  save: (v: Record<string, unknown>) => void;
  busy: boolean;
}): JSX.Element {
  const s = data.settings;
  const [v, setV] = useState({
    lists: String(s.listsHours),
    contacts: String(s.contactsHours),
    sent: String(s.sentMinutes),
    window: String(s.sentWindowDays),
    from: String(s.nightWindow.fromHour),
    to: String(s.nightWindow.toHour),
  });
  const [enabled, setEnabled] = useState(data.enabled);
  const [create, setCreate] = useState(s.createContacts);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV({ ...v, [k]: e.target.value });
  const num2 = (x: string): number => Number.parseInt(x, 10);
  return (
    <form
      className="grid2"
      onSubmit={(e) => {
        e.preventDefault();
        save({
          se_sync_enabled: enabled,
          se_sync_create_contacts: create,
          se_sync_lists_hours: num2(v.lists),
          se_sync_contacts_hours: num2(v.contacts),
          se_sync_sent_minutes: num2(v.sent),
          se_sync_sent_window_days: num2(v.window),
          se_sync_night_window: { fromHour: num2(v.from), toHour: num2(v.to) },
        });
      }}
    >
      <Field label="Automatická synchronizace" hint="Vypnuto = jen ruční spuštění.">
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label="Automatická synchronizace"
          disabled={!canWrite}
        />
      </Field>
      <Field
        label="Zakládat v hubu kontakty, které hub nezná"
        hint="Vznikají bez souhlasu. Vypnuto: kontakty ze SE zůstávají jen ve zrcadle."
      >
        <Toggle
          checked={create}
          onChange={setCreate}
          label="Zakládat neznámé kontakty"
          disabled={!canWrite}
        />
      </Field>
      <Field label="Seznamy: obnovit každých (hodin)" hint="1 až 168">
        <input
          type="number"
          min={1}
          max={168}
          value={v.lists}
          onChange={set('lists')}
          disabled={!canWrite}
        />
      </Field>
      <Field label="Kontakty: obnovit každých (hodin)" hint="1 až 168">
        <input
          type="number"
          min={1}
          max={168}
          value={v.contacts}
          onChange={set('contacts')}
          disabled={!canWrite}
        />
      </Field>
      <Field label="Odeslané e-maily: každých (minut)" hint="15 až 1440">
        <input
          type="number"
          min={15}
          max={1440}
          value={v.sent}
          onChange={set('sent')}
          disabled={!canWrite}
        />
      </Field>
      <Field label="Odeslané e-maily: okno (dnů zpět)" hint="1 až 365">
        <input
          type="number"
          min={1}
          max={365}
          value={v.window}
          onChange={set('window')}
          disabled={!canWrite}
        />
      </Field>
      <Field
        label="Noční okno od (hodina)"
        hint="Seznamy a kontakty se obnovují v noci; první běh a ruční spuštění okno obcházejí."
      >
        <input
          type="number"
          min={0}
          max={23}
          value={v.from}
          onChange={set('from')}
          disabled={!canWrite}
        />
      </Field>
      <Field label="Noční okno do (hodina)">
        <input
          type="number"
          min={0}
          max={24}
          value={v.to}
          onChange={set('to')}
          disabled={!canWrite}
        />
      </Field>
      {canWrite ? (
        <div>
          <button type="submit" className="btn pri" disabled={busy}>
            Uložit nastavení
          </button>
        </div>
      ) : null}
    </form>
  );
}
